// Stubs funcionais com tabelas + filtros + dialogs + toggle JSON.
// DrillPage foi migrada pra @/pages/drill/DrillPage.tsx (visualizacao rica com paginacao).

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Search as SearchIcon } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { EmptyState } from '@/components/common/EmptyState';
import { Loader } from '@/components/common/Loader';
import { JsonToggle } from '@/components/common/JsonToggle';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { dashboardsApi, drillApi, marksApi, casesApi, usersApi, notificationsApi, auditApi, reportsApi, changelogApi, configApi, themesApi, authApi } from '@/api/endpoints';
import { formatDate, bsonId } from '@/lib/utils';
import { Truncated } from '@/components/common/Truncated';
import { FilterBar } from '@/components/common/FilterBar';
import { DateRangePicker, presetRange } from '@/components/common/DateRangePicker';
import { toast } from '@/store/toast';
import { useAuthStore } from '@/store/auth';

// ============ DETECTIONS LIST ============
// Categorias macro vindas do bySource do rollup top_cheaters_7d:
// - cheat: bySource.detection_cheat > 0 (TAMPER_DETECTION, MEMORY_DETECTION etc.)
// - dll:   bySource.detection_dll   > 0 (LIBRARY_DETECTION)
// - files: bySource.detection_files > 0 (FILE_DETECTION)
const CATEGORY_KEY: Record<string, string> = {
  cheat: 'detection_cheat',
  dll: 'detection_dll',
  files: 'detection_files'
};

export function DetectionsPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(100);
  const [category, setCategory] = useState(ALL);       // cheat | dll | files | ALL
  const [detType, setDetType] = useState(ALL);          // dinamico, derivado dos dados
  const [minRisk, setMinRisk] = useState('');
  const [minDetections, setMinDetections] = useState('');
  const [onlyMarked, setOnlyMarked] = useState(false);
  const [sortBy, setSortBy] = useState<'detections' | 'risk' | 'recent'>('detections');

  useEffect(() => {
    setLoading(true);
    dashboardsApi.topCheaters(limit).then(setData).finally(() => setLoading(false));
  }, [limit]);

  // Tipos de detecção reais presentes no dado carregado (dinamico)
  const detTypesAvailable = Array.from(new Set(
    data.flatMap(d => (d.detectionTypes ?? []) as string[])
  )).sort();

  // Cada cheater pode ter detecções em arquivos já marcados como ALLOWLIST. O backend
  // entrega 2 metricas:
  //   - totalDetections / bySource          -> contagem bruta (inclui allowlist)
  //   - totalDetectionsUnknown / bySourceUnknown -> contagem util (sem allowlist)
  // Para ordenação, filtro de "min detecções" e categoria, usamos a metrica SEM
  // allowlist (a "real"). A coluna de allowlist é exibida como info paralela.
  const effective = (d: any) => d.totalDetectionsUnknown ?? d.totalDetections ?? 0;
  const effectiveBySource = (d: any, key: string) =>
    (d.bySourceUnknown?.[key] ?? d.bySource?.[key] ?? 0);

  const filtered = data
    .filter(d => !search || JSON.stringify(d).toLowerCase().includes(search.toLowerCase()))
    .filter(d => {
      if (category === ALL) return true;
      const key = CATEGORY_KEY[category];
      return effectiveBySource(d, key) > 0;
    })
    .filter(d => detType === ALL || (d.detectionTypes ?? []).includes(detType))
    .filter(d => !minRisk || (d.riskScoreMax ?? 0) >= Number(minRisk))
    .filter(d => !minDetections || effective(d) >= Number(minDetections))
    .filter(d => !onlyMarked || d.mark)
    .sort((a, b) => {
      if (sortBy === 'risk') return (b.riskScoreMax ?? 0) - (a.riskScoreMax ?? 0);
      if (sortBy === 'recent') return new Date(b.lastSeenAtUtc ?? 0).getTime() - new Date(a.lastSeenAtUtc ?? 0).getTime();
      if (category !== ALL) {
        const k = CATEGORY_KEY[category];
        return effectiveBySource(b, k) - effectiveBySource(a, k);
      }
      return effective(b) - effective(a);
    });

  function clearAll() { setSearch(''); setCategory(ALL); setDetType(ALL); setMinRisk(''); setMinDetections(''); setOnlyMarked(false); setSortBy('detections'); }
  const active = [
    search && { key: 's', label: t('ptDetections.chipSearch'), value: search, onClear: () => setSearch('') },
    category !== ALL && { key: 'c', label: t('ptDetections.chipCategory'), value: category === 'cheat' ? t('ptDetections.catCheat') : category === 'dll' ? t('ptDetections.catDll') : t('ptDetections.catFiles'), onClear: () => setCategory(ALL) },
    detType !== ALL && { key: 'dt', label: t('ptDetections.chipType'), value: detType, onClear: () => setDetType(ALL) },
    minRisk && { key: 'r', label: t('ptDetections.chipRisk'), value: `≥ ${minRisk}`, onClear: () => setMinRisk('') },
    minDetections && { key: 'd', label: t('ptDetections.chipDetections'), value: `≥ ${minDetections}`, onClear: () => setMinDetections('') },
    onlyMarked && { key: 'm', label: t('ptDetections.chipMarked'), value: t('ptDetections.yes'), onClear: () => setOnlyMarked(false) }
  ].filter(Boolean) as { key: string; label: string; value: string; onClear: () => void }[];

  return (
    <div>
      <PageHeader
        title={t('nav.detections')}
        subtitle={t('ptDetections.subtitle')}
        actions={<JsonToggle data={data} label={t('ptUsers.viewJson')} />}
      />
      <FilterBar active={active} onClearAll={clearAll}>
        <Input className="h-8 w-56" placeholder={t('ptDetections.searchPlaceholder')} value={search} onChange={e => setSearch(e.target.value)} />
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="h-8 w-40 text-xs"><SelectValue placeholder={t('ptDetections.filterCategory')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('ptDetections.filterAllCategories')}</SelectItem>
            <SelectItem value="cheat">{t('ptDetections.catCheat')}</SelectItem>
            <SelectItem value="dll">{t('ptDetections.catDll')}</SelectItem>
            <SelectItem value="files">{t('ptDetections.catFiles')}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={detType} onValueChange={setDetType}>
          <SelectTrigger className="h-8 w-52 text-xs"><SelectValue placeholder={t('ptDetections.filterDetectionType')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('ptDetections.filterAllTypes')}</SelectItem>
            {detTypesAvailable.length === 0
              ? <SelectItem value="__none__" disabled>{t('ptDetections.loadingTypes')}</SelectItem>
              : detTypesAvailable.map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input className="h-8 w-28" type="number" min={0} placeholder={t('ptDetections.minRiskPlaceholder')} value={minRisk} onChange={e => setMinRisk(e.target.value)} />
        <Input className="h-8 w-32" type="number" min={0} placeholder={t('ptDetections.minDetectionsPlaceholder')} value={minDetections} onChange={e => setMinDetections(e.target.value)} />
        <label className="flex items-center gap-2 text-xs whitespace-nowrap">
          <input type="checkbox" checked={onlyMarked} onChange={e => setOnlyMarked(e.target.checked)} />
          {t('ptDetections.onlyMarked')}
        </label>
        <Select value={sortBy} onValueChange={v => setSortBy(v as any)}>
          <SelectTrigger className="h-8 w-48 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="detections">{t('ptDetections.sortDetections')}</SelectItem>
            <SelectItem value="risk">{t('ptDetections.sortRisk')}</SelectItem>
            <SelectItem value="recent">{t('ptDetections.sortRecent')}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={String(limit)} onValueChange={v => setLimit(Number(v))}>
          <SelectTrigger className="h-8 w-28 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{[50, 100, 250, 500].map(n => <SelectItem key={n} value={String(n)}>{n} {t('ptDetections.items')}</SelectItem>)}</SelectContent>
        </Select>
        <Badge variant="outline" className="ml-1">{filtered.length} {t('ptDetections.of')} {data.length}</Badge>
      </FilterBar>
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('ptDetections.colIndex')}</TableHead>
              <TableHead>{t('ptDetections.colHwid')}</TableHead>
              <TableHead>{t('ptDetections.colDetections')}</TableHead>
              <TableHead>{t('ptDetections.colCategories')}</TableHead>
              <TableHead>{t('ptDetections.colMaxRisk')}</TableHead>
              <TableHead>{t('ptDetections.colDetectionTypes')}</TableHead>
              <TableHead>{t('ptDetections.colLastDetection')}</TableHead>
              <TableHead className="sr-only">{t('ptDetections.colActions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={8}><Loader /></TableCell></TableRow>}
            {!loading && filtered.length === 0 && <TableRow><TableCell colSpan={8}><EmptyState /></TableCell></TableRow>}
            {filtered.map((c, i) => {
              // bySourceUnknown (sem allowlist) é a métrica usada na UI; bySource bruto
              // fica disponível só pra tooltip mostrar quanto foi filtrado
              const bsU = c.bySourceUnknown ?? c.bySource ?? {};
              const bsRaw = c.bySource ?? {};
              const cheat = bsU.detection_cheat ?? 0;
              const dll = bsU.detection_dll ?? 0;
              const files = bsU.detection_files ?? 0;
              const total = c.totalDetectionsUnknown ?? c.totalDetections ?? 0;
              const allowlisted = c.allowlistedFilesCount ?? 0;
              const filesRaw = bsRaw.detection_files ?? 0;
              return (
                <TableRow key={c.hwid || i}>
                  <TableCell className="tabular-nums text-muted-foreground">{i + 1}</TableCell>
                  <TableCell className="text-xs"><Truncated mono value={c.hwid} maxChars={28} /></TableCell>
                  <TableCell className="font-bold">
                    {total}
                    {allowlisted > 0 && (
                      <span
                        className="ml-2 text-xs font-normal text-muted-foreground"
                        title={t('ptDetections.allowlistTooltip', { allowlisted, raw: c.totalDetections ?? 0 })}
                      >
                        (+{allowlisted})
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">
                    <div className="flex flex-wrap gap-1">
                      {cheat > 0 && <Badge variant="destructive" title={t('ptDetections.badgeCheatTitle')}>{t('ptDetections.catCheat')} {cheat}</Badge>}
                      {dll > 0 && <Badge variant="warning" title={t('ptDetections.badgeDllTitle')}>{t('ptDetections.catDll')} {dll}</Badge>}
                      {files > 0 && <Badge variant="secondary" title={`${t('ptDetections.badgeFilesTitle')}${allowlisted > 0 ? ` (${filesRaw} brutos, ${allowlisted} em allowlist)` : ''}`}>{t('ptDetections.badgeFilesShort')} {files}</Badge>}
                      {allowlisted > 0 && files === 0 && (
                        <Badge variant="outline" title={t('ptDetections.badgeAllowlistedTitle')} className="text-emerald-700 border-emerald-600/50">
                          {t('ptDetections.badgeAllowlisted')} {allowlisted}
                        </Badge>
                      )}
                      {cheat + dll + files + allowlisted === 0 && <span className="text-muted-foreground">—</span>}
                    </div>
                  </TableCell>
                  <TableCell><Badge variant="destructive">{c.riskScoreMax ?? 0}</Badge></TableCell>
                  <TableCell className="text-xs">{(c.detectionTypes ?? []).slice(0, 3).join(', ')}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{formatDate(c.lastSeenAtUtc)}</TableCell>
                  <TableCell>
                    <Link to={`/drill?type=hwid&value=${encodeURIComponent(c.hwid)}`}>
                      <Button size="sm" variant="ghost"><SearchIcon className="h-3 w-3" /></Button>
                    </Link>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// ============ MARKS ============
// Radix Select proibe SelectItem com value="" — usamos __ALL__ como sentinela
const ALL = '__ALL__';

const MARK_ENTITY_TYPES = ['hwid','process_name','file_md5','source_hash','module_path','ip_address','user_id','nickname'];
const MARK_STATUSES = ['SUSPECT','CLEAN','KNOWN_CHEATER','FALSE_POSITIVE','INVESTIGATING','EXEMPT','ALLOWLIST'];

export function MarksPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>(ALL);
  const [typeFilter, setTypeFilter] = useState<string>(ALL);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [minSeverity, setMinSeverity] = useState<string>('');
  const [from, setFrom] = useState<string>();
  const [to, setTo] = useState<string>();
  const [open, setOpen] = useState(false);

  useEffect(() => { const t = setTimeout(() => setSearchDebounced(search.trim()), 400); return () => clearTimeout(t); }, [search]);

  async function reload() {
    setLoading(true);
    const res = await marksApi.search({
      entityType: typeFilter === ALL ? undefined : typeFilter,
      status: statusFilter === ALL ? undefined : statusFilter,
      search: searchDebounced || undefined,
      minSeverity: minSeverity ? Number(minSeverity) : undefined,
      from, to,
      pageSize: 100
    });
    setItems(res.items);
    setTotal(res.totalApprox);
    setLoading(false);
  }
  useEffect(() => { reload(); }, [statusFilter, typeFilter, searchDebounced, minSeverity, from, to]);

  function clearAll() {
    setStatusFilter(ALL); setTypeFilter(ALL); setSearch(''); setMinSeverity(''); setFrom(undefined); setTo(undefined);
  }

  const active = [
    typeFilter !== ALL && { key: 'type', label: t('ptMarks.chipType'), value: typeFilter, onClear: () => setTypeFilter(ALL) },
    statusFilter !== ALL && { key: 'status', label: t('ptMarks.chipStatus'), value: statusFilter, onClear: () => setStatusFilter(ALL) },
    searchDebounced && { key: 'search', label: t('ptMarks.chipSearch'), value: searchDebounced, onClear: () => setSearch('') },
    minSeverity && { key: 'sev', label: t('ptMarks.chipSeverity'), value: `≥ ${minSeverity}`, onClear: () => setMinSeverity('') },
    from && { key: 'from', label: t('ptMarks.chipFrom'), value: formatDate(from, 'dd/MM HH:mm'), onClear: () => setFrom(undefined) },
    to && { key: 'to', label: t('ptMarks.chipTo'), value: formatDate(to, 'dd/MM HH:mm'), onClear: () => setTo(undefined) }
  ].filter(Boolean) as { key: string; label: string; value: string; onClear: () => void }[];

  return (
    <div>
      <PageHeader title={t('marks.title')} subtitle={t('ptMarks.subtitle', { count: total })} actions={
        <div className="flex gap-2">
          <JsonToggle data={items} label={t('ptUsers.viewJson')} />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button>+ {t('marks.create')}</Button></DialogTrigger>
            <MarkFormDialog onSaved={() => { setOpen(false); reload(); }} />
          </Dialog>
        </div>
      } />
      <FilterBar active={active} onClearAll={clearAll}>
        <Input className="h-8 w-56" placeholder={t('ptMarks.searchPlaceholder')} value={search} onChange={e => setSearch(e.target.value)} />
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="h-8 w-44 text-xs"><SelectValue placeholder={t('ptMarks.filterType')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('ptMarks.filterAllTypes')}</SelectItem>
            {MARK_ENTITY_TYPES.map(x => <SelectItem key={x} value={x}>{t(`marks.entityTypes.${x}`)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-8 w-44 text-xs"><SelectValue placeholder={t('ptMarks.filterStatus')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('ptMarks.filterAllStatuses')}</SelectItem>
            {MARK_STATUSES.map(x => <SelectItem key={x} value={x}>{t(`marks.statuses.${x}`)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input className="h-8 w-28" type="number" min={0} max={10} placeholder={t('ptMarks.minSeverityPlaceholder')} value={minSeverity} onChange={e => setMinSeverity(e.target.value)} />
        <DateRangePicker from={from} to={to} onChange={r => { setFrom(r.from); setTo(r.to); }} label={t('ptMarks.dateLabel')} />
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" onClick={() => { const r = presetRange('last24h'); setFrom(r.from); setTo(r.to); }}>{t('ptMarks.preset24h')}</Button>
          <Button size="sm" variant="ghost" onClick={() => { const r = presetRange('last7d'); setFrom(r.from); setTo(r.to); }}>{t('ptMarks.preset7d')}</Button>
          <Button size="sm" variant="ghost" onClick={() => { const r = presetRange('last30d'); setFrom(r.from); setTo(r.to); }}>{t('ptMarks.preset30d')}</Button>
        </div>
      </FilterBar>
      <Card>
        <Table>
          <TableHeader><TableRow>
            <TableHead>{t('ptMarks.colType')}</TableHead><TableHead>{t('ptMarks.colValue')}</TableHead><TableHead>{t('ptMarks.colStatus')}</TableHead>
            <TableHead>{t('ptMarks.colSeverity')}</TableHead><TableHead>{t('ptMarks.colTags')}</TableHead><TableHead>{t('ptMarks.colCreatedBy')}</TableHead><TableHead>{t('ptMarks.colUpdatedAt')}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={7}><Loader /></TableCell></TableRow>}
            {!loading && items.length === 0 && <TableRow><TableCell colSpan={7}><EmptyState /></TableCell></TableRow>}
            {items.map((m, i) => (
              <TableRow key={bsonId(m._id, i)}>
                <TableCell><Badge variant="outline">{String(t(`marks.entityTypes.${m.entityType}`, { defaultValue: m.entityType }))}</Badge></TableCell>
                <TableCell className="text-xs"><Truncated mono value={m.entityValue} maxChars={32} /></TableCell>
                <TableCell><Badge className={`badge-status-${(m.status||'').toLowerCase().replace('_','-')}`}>{String(t(`marks.statuses.${m.status}`, { defaultValue: m.status }))}</Badge></TableCell>
                <TableCell className="font-bold">{m.severity}</TableCell>
                <TableCell className="text-xs">{(m.tags ?? []).join(', ')}</TableCell>
                <TableCell className="text-xs">{m.createdByEmail || '—'}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{formatDate(m.updatedAtUtc)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function MarkFormDialog({ onSaved }: { onSaved: () => void }) {
  const { t } = useTranslation();
  const [entityType, setEntityType] = useState('hwid');
  const [entityValue, setEntityValue] = useState('');
  const [status, setStatus] = useState('SUSPECT');
  const [severity, setSeverity] = useState(5);
  const [tags, setTags] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  async function save() {
    if (!entityValue.trim()) return;
    setLoading(true);
    try {
      await marksApi.upsert({ entityType, entityValue, status, severity, tags: tags.split(',').map(s => s.trim()).filter(Boolean), notes });
      toast.success(t('ptMarks.saveOk')); onSaved();
    } catch (e: any) { toast.error(t('ptMarks.saveFailed'), e.message); }
    finally { setLoading(false); }
  }
  return (
    <DialogContent>
      <DialogHeader><DialogTitle>{t('ptMarks.dialogTitle')}</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label>{t('ptMarks.dialogType')}</Label>
            <Select value={entityType} onValueChange={setEntityType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{['hwid','process_name','file_md5','source_hash','module_path','ip_address','user_id','nickname'].map(x => <SelectItem key={x} value={x}>{t(`marks.entityTypes.${x}`)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t('ptMarks.dialogStatus')}</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{['SUSPECT','CLEAN','KNOWN_CHEATER','FALSE_POSITIVE','INVESTIGATING','EXEMPT','ALLOWLIST'].map(x => <SelectItem key={x} value={x}>{t(`marks.statuses.${x}`)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div><Label>{t('ptMarks.dialogValue')}</Label><Input value={entityValue} onChange={e => setEntityValue(e.target.value)} placeholder={t('ptMarks.dialogValuePlaceholder')} /></div>
        <div><Label>{t('ptMarks.dialogSeverity')}</Label><Input type="number" min={0} max={10} value={severity} onChange={e => setSeverity(Number(e.target.value))} /></div>
        <div><Label>{t('ptMarks.dialogTags')}</Label><Input value={tags} onChange={e => setTags(e.target.value)} placeholder={t('ptMarks.dialogTagsPlaceholder')} /></div>
        <div><Label>{t('ptMarks.dialogNotes')}</Label><Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder={t('ptMarks.dialogNotesPlaceholder')} /></div>
      </div>
      <DialogFooter><Button onClick={save} disabled={loading}>{loading ? <Loader /> : t('ptMarks.saveBtn')}</Button></DialogFooter>
    </DialogContent>
  );
}

// ============ CASES ============
export function CasesPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(ALL);
  const [minPriority, setMinPriority] = useState('');
  const [targetEntityType, setTargetEntityType] = useState(ALL);
  const [assignedToUserId, setAssignedToUserId] = useState('');
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  useEffect(() => { const t = setTimeout(() => setSearchDebounced(search.trim()), 400); return () => clearTimeout(t); }, [search]);

  useEffect(() => {
    setLoading(true);
    casesApi.search({
      status: statusFilter === ALL ? undefined : statusFilter,
      minPriority: minPriority ? Number(minPriority) : undefined,
      targetEntityType: targetEntityType === ALL ? undefined : targetEntityType,
      targetEntityValue: searchDebounced || undefined,
      assignedToUserId: assignedToUserId || undefined,
      pageSize: 100
    }).then(r => { setItems(r.items); setTotal(r.totalApprox ?? r.items.length); setLoading(false); });
  }, [statusFilter, minPriority, targetEntityType, searchDebounced, assignedToUserId]);

  function clearAll() {
    setStatusFilter(ALL); setMinPriority(''); setTargetEntityType(ALL); setAssignedToUserId(''); setSearch('');
  }
  const active = [
    statusFilter !== ALL && { key: 's', label: t('ptCases.chipStatus'), value: statusFilter, onClear: () => setStatusFilter(ALL) },
    minPriority && { key: 'p', label: t('ptCases.chipPriority'), value: `≥ ${minPriority}`, onClear: () => setMinPriority('') },
    targetEntityType !== ALL && { key: 'tt', label: t('ptCases.chipTargetType'), value: targetEntityType, onClear: () => setTargetEntityType(ALL) },
    searchDebounced && { key: 'tv', label: t('ptCases.chipTargetValue'), value: searchDebounced, onClear: () => setSearch('') },
    assignedToUserId && { key: 'a', label: t('ptCases.chipAssignedTo'), value: assignedToUserId, onClear: () => setAssignedToUserId('') }
  ].filter(Boolean) as { key: string; label: string; value: string; onClear: () => void }[];

  return (
    <div>
      <PageHeader title={t('cases.title')} subtitle={t('ptCases.subtitle', { count: total })} actions={
        <div className="flex gap-2">
          <JsonToggle data={items} label={t('ptUsers.viewJson')} />
          <Button disabled title={t('ptCases.soon')}>+ {t('cases.create')}</Button>
        </div>
      } />
      <FilterBar active={active} onClearAll={clearAll}>
        <Input className="h-8 w-56" placeholder={t('ptCases.searchPlaceholder')} value={search} onChange={e => setSearch(e.target.value)} />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-8 w-44 text-xs"><SelectValue placeholder={t('ptCases.filterStatus')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('ptCases.filterAllStatuses')}</SelectItem>
            {['OPEN','IN_REVIEW','ESCALATED','RESOLVED','CLOSED_NO_ACTION'].map(x => <SelectItem key={x} value={x}>{t(`cases.statuses.${x}`)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={targetEntityType} onValueChange={setTargetEntityType}>
          <SelectTrigger className="h-8 w-40 text-xs"><SelectValue placeholder={t('ptCases.filterTargetType')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('ptCases.filterAnyTargetType')}</SelectItem>
            {MARK_ENTITY_TYPES.map(x => <SelectItem key={x} value={x}>{t(`marks.entityTypes.${x}`)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input className="h-8 w-32" type="number" min={0} max={10} placeholder={t('ptCases.minPriorityPlaceholder')} value={minPriority} onChange={e => setMinPriority(e.target.value)} />
        <Input className="h-8 w-44" placeholder={t('ptCases.assignedPlaceholder')} value={assignedToUserId} onChange={e => setAssignedToUserId(e.target.value)} />
      </FilterBar>
      <Card><Table>
        <TableHeader><TableRow>
          <TableHead>{t('ptCases.colCaseNumber')}</TableHead><TableHead>{t('ptCases.colStatus')}</TableHead><TableHead>{t('ptCases.colPriority')}</TableHead>
          <TableHead>{t('ptCases.colTitle')}</TableHead><TableHead>{t('ptCases.colTarget')}</TableHead><TableHead>{t('ptCases.colAssignedTo')}</TableHead><TableHead>{t('ptCases.colUpdated')}</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {loading && <TableRow><TableCell colSpan={7}><Loader /></TableCell></TableRow>}
          {!loading && items.length === 0 && <TableRow><TableCell colSpan={7}><EmptyState /></TableCell></TableRow>}
          {items.map((c, i) => (
            <TableRow key={bsonId(c._id, i)}>
              <TableCell className="font-mono text-xs">{c.caseNumber ?? '—'}</TableCell>
              <TableCell><Badge>{c.status ? String(t(`cases.statuses.${c.status}`, { defaultValue: c.status })) : '—'}</Badge></TableCell>
              <TableCell>{c.priority ?? '—'}</TableCell>
              <TableCell>{c.title ?? '—'}</TableCell>
              <TableCell className="text-xs"><span className="font-mono text-muted-foreground">{c.targetEntityType ? String(t(`marks.entityTypes.${c.targetEntityType}`, { defaultValue: c.targetEntityType })) : '—'}:</span> <Truncated mono value={c.targetEntityValue} maxChars={24} /></TableCell>
              <TableCell className="text-xs">{c.assignedToUserId || '—'}</TableCell>
              <TableCell className="text-xs text-muted-foreground">{formatDate(c.updatedAtUtc)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table></Card>
    </div>
  );
}

// ============ USERS (admin) ============
export function UsersPage() {
  const { t } = useTranslation();
  const [data, setData] = useState<any>({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [role, setRole] = useState(ALL);
  const [active, setActive] = useState(ALL); // ALL | 'true' | 'false'
  const [totp, setTotp] = useState(ALL);
  const [createOpen, setCreateOpen] = useState(false);
  useEffect(() => { const t = setTimeout(() => setSearchDebounced(search.trim()), 400); return () => clearTimeout(t); }, [search]);

  function reload() {
    setLoading(true);
    usersApi.list({
      search: searchDebounced || undefined,
      role: role === ALL ? undefined : role,
      isActive: active === ALL ? undefined : active === 'true',
      isTotpEnabled: totp === ALL ? undefined : totp === 'true',
      pageSize: 100
    }).then(d => { setData(d); setLoading(false); });
  }
  useEffect(() => { reload(); }, [searchDebounced, role, active, totp]);

  function clearAll() { setSearch(''); setRole(ALL); setActive(ALL); setTotp(ALL); }
  const activeChips = [
    searchDebounced && { key: 'q', label: t('ptUsers.chipSearch'), value: searchDebounced, onClear: () => setSearch('') },
    role !== ALL && { key: 'r', label: t('ptUsers.chipRole'), value: role === 'admin' ? t('users.roles.admin') : role === 'manager' ? t('users.roles.manager') : role, onClear: () => setRole(ALL) },
    active !== ALL && { key: 'a', label: t('ptUsers.chipActive'), value: active === 'true' ? t('ptUsers.yesShort') : t('ptUsers.noShort'), onClear: () => setActive(ALL) },
    totp !== ALL && { key: 't', label: t('ptUsers.chip2FA'), value: totp === 'true' ? t('ptUsers.yesShort') : t('ptUsers.noShort'), onClear: () => setTotp(ALL) }
  ].filter(Boolean) as { key: string; label: string; value: string; onClear: () => void }[];

  return (
    <div>
      <PageHeader title={t('users.title')} subtitle={t('ptUsers.subtitle', { count: data.total ?? 0 })} actions={
        <div className="flex gap-2">
          <JsonToggle data={data.items} label={t('ptUsers.viewJson')} />
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild><Button>+ {t('users.create')}</Button></DialogTrigger>
            <UserFormDialog onSaved={() => { setCreateOpen(false); reload(); }} />
          </Dialog>
        </div>
      } />
      <FilterBar active={activeChips} onClearAll={clearAll}>
        <Input className="h-8 w-64" placeholder={t('ptUsers.searchPlaceholder')} value={search} onChange={e => setSearch(e.target.value)} />
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="h-8 w-40 text-xs"><SelectValue placeholder={t('ptUsers.filterRole')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('ptUsers.filterRoleAny')}</SelectItem>
            <SelectItem value="admin">{t('users.roles.admin')}</SelectItem>
            <SelectItem value="manager">{t('users.roles.manager')}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={active} onValueChange={setActive}>
          <SelectTrigger className="h-8 w-40 text-xs"><SelectValue placeholder={t('ptUsers.filterStatus')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('ptUsers.filterStatusAny')}</SelectItem>
            <SelectItem value="true">{t('ptUsers.filterStatusOnlyActive')}</SelectItem>
            <SelectItem value="false">{t('ptUsers.filterStatusOnlyInactive')}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={totp} onValueChange={setTotp}>
          <SelectTrigger className="h-8 w-40 text-xs"><SelectValue placeholder={t('ptUsers.chip2FA')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('ptUsers.filterTotpAny')}</SelectItem>
            <SelectItem value="true">{t('ptUsers.filterTotpYes')}</SelectItem>
            <SelectItem value="false">{t('ptUsers.filterTotpNo')}</SelectItem>
          </SelectContent>
        </Select>
      </FilterBar>
      <Card><Table>
        <TableHeader><TableRow><TableHead>{t('ptUsers.colEmail')}</TableHead><TableHead>{t('ptUsers.colName')}</TableHead><TableHead>{t('ptUsers.colRole')}</TableHead><TableHead>{t('ptUsers.colStatus')}</TableHead><TableHead>{t('ptUsers.col2FA')}</TableHead><TableHead>{t('ptUsers.colLastLogin')}</TableHead><TableHead className="text-right">{t('ptUsers.colActions')}</TableHead></TableRow></TableHeader>
        <TableBody>
          {loading && <TableRow><TableCell colSpan={7}><Loader /></TableCell></TableRow>}
          {!loading && data.items.length === 0 && <TableRow><TableCell colSpan={7}><EmptyState /></TableCell></TableRow>}
          {data.items.map((u: any, i: number) => (
            <TableRow key={bsonId(u._id ?? u.id, i)}>
              <TableCell>{u.email ?? '—'}</TableCell>
              <TableCell>{u.displayName ?? '—'}</TableCell>
              <TableCell><Badge variant={u.role === 'admin' ? 'destructive' : 'secondary'}>{u.role === 'admin' ? t('users.roles.admin') : (u.role === 'manager' ? t('users.roles.manager') : (u.role ?? '—'))}</Badge></TableCell>
              <TableCell>
                {u.isPending
                  ? <Badge className="bg-amber-500/20 text-amber-700 border-amber-500/50">{t('ptUsers.statusPending')}</Badge>
                  : (u.isActive ? <Badge variant="success">{t('ptUsers.statusActive')}</Badge> : <Badge variant="outline">{t('ptUsers.statusInactive')}</Badge>)}
              </TableCell>
              <TableCell>{u.isTotpEnabled ? <Badge variant="info">✓</Badge> : <Badge variant="outline">—</Badge>}</TableCell>
              <TableCell className="text-xs text-muted-foreground">{u.lastLoginAtUtc ? formatDate(u.lastLoginAtUtc) : '—'}</TableCell>
              <TableCell className="text-right"><UserRowActions user={u} onChanged={reload} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table></Card>
    </div>
  );
}

// Acoes por linha da tabela de usuarios:
//   - Pendente: reenviar/capturar convite + cancelar convite
//   - Ativo:    remover usuario (com confirmacao textual)
function UserRowActions({ user, onChanged }: { user: any; onChanged: () => void }) {
  const { t } = useTranslation();
  const { user: me } = useAuthStore();
  const [resendOpen, setResendOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const isSelf = me?.userId === (user.id ?? user._id);

  return (
    <div className="flex items-center justify-end gap-1">
      {user.isPending ? (
        <>
          <Button size="sm" variant="ghost" title={t('ptUsers.actionResendTitle')} onClick={() => setResendOpen(true)}>
            {t('ptUsers.actionResend')}
          </Button>
          <Button size="sm" variant="ghost" className="text-destructive" title={t('ptUsers.actionCancelTitle')} onClick={() => setCancelOpen(true)}>
            {t('ptUsers.actionCancel')}
          </Button>
        </>
      ) : (
        <Button size="sm" variant="ghost" className="text-destructive" title={t('ptUsers.actionRemoveTitle')}
                disabled={isSelf} onClick={() => setRemoveOpen(true)}>
          {t('ptUsers.actionRemove')}
        </Button>
      )}
      {resendOpen && <ResendInviteDialog user={user} onClose={() => setResendOpen(false)} onDone={() => { setResendOpen(false); onChanged(); }} />}
      {cancelOpen && <CancelInviteDialog user={user} onClose={() => setCancelOpen(false)} onDone={() => { setCancelOpen(false); onChanged(); }} />}
      {removeOpen && <RemoveUserDialog user={user} onClose={() => setRemoveOpen(false)} onDone={() => { setRemoveOpen(false); onChanged(); }} />}
    </div>
  );
}

function ResendInviteDialog({ user, onClose, onDone }: { user: any; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ url: string; expiresAtUtc: string; emailSent: boolean } | null>(null);

  async function submit() {
    setBusy(true);
    try {
      const r = await usersApi.resendInvite(user.id ?? user._id);
      setResult(r);
      toast.success(t('ptUsers.resendOk'), r.emailSent ? t('ptUsers.resendEmailSent') : t('ptUsers.resendEmailNotSent'));
    } catch (e: any) {
      toast.error(t('ptUsers.resendFailed'), e?.response?.data?.reason || e?.message);
    } finally {
      setBusy(false);
    }
  }

  function copyUrl() {
    if (!result) return;
    navigator.clipboard.writeText(result.url).then(() => toast.success(t('ptUsers.linkCopied')));
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) (result ? onDone() : onClose()); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('ptUsers.resendTitle')}</DialogTitle>
          <DialogDescription>{t('ptUsers.resendDesc', { email: user.email })}</DialogDescription>
        </DialogHeader>
        {!result ? (
          <DialogFooter>
            <Button variant="ghost" onClick={onClose} disabled={busy}>{t('common.cancel')}</Button>
            <Button onClick={submit} disabled={busy}>{busy ? t('ptUsers.resendGenerating') : t('ptUsers.resendGenerate')}</Button>
          </DialogFooter>
        ) : (
          <div className="space-y-3">
            <div className="bg-muted/50 rounded p-3 text-xs">
              <div className="text-muted-foreground mb-1">{t('ptUsers.resendLinkLabel', { time: new Date(result.expiresAtUtc).toLocaleString() })}</div>
              <div className="font-mono break-all">{result.url}</div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={copyUrl} className="flex-1">{t('ptUsers.resendCopy')}</Button>
              <Button onClick={onDone} className="flex-1">{t('ptUsers.resendClose')}</Button>
            </div>
            <div className="text-xs text-muted-foreground">
              {result.emailSent ? t('ptUsers.resendAlsoEmail') : t('ptUsers.resendOnlyLink')}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CancelInviteDialog({ user, onClose, onDone }: { user: any; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  async function submit() {
    setBusy(true);
    try {
      await usersApi.cancelInvite(user.id ?? user._id);
      toast.success(t('ptUsers.cancelOk'), t('ptUsers.cancelOkHint', { email: user.email }));
      onDone();
    } catch (e: any) {
      toast.error(t('ptUsers.cancelFailed'), e?.response?.data?.reason || e?.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('ptUsers.cancelTitle')}</DialogTitle>
          <DialogDescription>{t('ptUsers.cancelDesc', { email: user.email })}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>{t('ptUsers.cancelBack')}</Button>
          <Button variant="destructive" onClick={submit} disabled={busy}>{busy ? t('common.cancel') + '…' : t('ptUsers.cancelConfirm')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RemoveUserDialog({ user, onClose, onDone }: { user: any; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const canSubmit = confirm.trim().toLowerCase() === (user.email ?? '').toLowerCase();
  async function submit() {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await usersApi.remove(user.id ?? user._id);
      toast.success(t('ptUsers.removeOk'), user.email);
      onDone();
    } catch (e: any) {
      toast.error(t('ptUsers.removeFailed'), e?.response?.data?.reason || e?.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-destructive">{t('ptUsers.removeTitle')}</DialogTitle>
          <DialogDescription>{t('ptUsers.removeDesc', { email: user.email })}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-sm">
          <Label htmlFor="confirm">{t('ptUsers.removeConfirmLabel')}</Label>
          <Input id="confirm" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder={user.email} autoFocus />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>{t('common.cancel')}</Button>
          <Button variant="destructive" onClick={submit} disabled={!canSubmit || busy}>{busy ? t('ptUsers.removeRunning') : t('ptUsers.removeBtn')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UserFormDialog({ onSaved }: { onSaved: () => void }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<'admin' | 'manager'>('manager');
  const [loading, setLoading] = useState(false);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (loading) return;
    if (!email.trim() || !displayName.trim()) {
      toast.error(t('ptUserInvite.incomplete'), t('ptUserInvite.incompleteHint'));
      return;
    }
    setLoading(true);
    try {
      const result = await authApi.inviteUser({
        email: email.trim().toLowerCase(),
        displayName: displayName.trim(),
        role,
        portalUrl: window.location.origin
      });
      toast.success(
        t('ptUserInvite.sent'),
        t('ptUserInvite.sentHint', { email: result.email, date: new Date(result.inviteExpiresAtUtc).toLocaleDateString() })
      );
      onSaved();
    } catch (e: any) {
      const reason = e?.response?.data?.reason;
      const friendly = (reason === 'email_already_used' || reason === 'email_already_in_use')
          ? t('ptUserInvite.errEmailUsed')
        : reason === 'invalid_role' ? t('ptUserInvite.errInvalidRole')
        : reason === 'invalid_email' ? t('ptUserInvite.errInvalidEmail')
        : reason === 'email_and_displayName_required' ? t('ptUserInvite.errRequired')
        : reason ?? e.message ?? t('ptUserInvite.errUnexpected');
      toast.error(t('ptUserInvite.failed'), friendly);
    } finally {
      setLoading(false);
    }
  }

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{t('ptUserInvite.dialogTitle')}</DialogTitle>
      </DialogHeader>
      <form onSubmit={save} className="space-y-3">
        <div>
          <Label>{t('ptUserInvite.emailLabel')} <span className="text-destructive">*</span></Label>
          <Input type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder={t('ptUserInvite.emailPlaceholder')} autoFocus />
          <div className="text-xs text-muted-foreground mt-1">{t('ptUserInvite.emailHint')}</div>
        </div>
        <div>
          <Label>{t('ptUserInvite.nameLabel')} <span className="text-destructive">*</span></Label>
          <Input value={displayName} onChange={e => setDisplayName(e.target.value)} required placeholder={t('ptUserInvite.namePlaceholder')} />
        </div>
        <div>
          <Label>{t('ptUserInvite.roleLabel')}</Label>
          <Select value={role} onValueChange={v => setRole(v as 'admin' | 'manager')}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="manager">{t('ptUserInvite.roleManagerDesc')}</SelectItem>
              <SelectItem value="admin">{t('ptUserInvite.roleAdminDesc')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="text-xs text-muted-foreground bg-muted/40 border-l-2 border-info p-2 rounded">
          {t('ptUserInvite.infoBox')}
        </div>
        <DialogFooter>
          <Button type="submit" disabled={loading}>{loading ? <Loader /> : t('ptUserInvite.submit')}</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

// ============ NOTIFICATIONS ============
export function NotificationsPage() {
  const { t } = useTranslation();
  const [subs, setSubs] = useState<any[]>([]);
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [channel, setChannel] = useState(ALL);
  const [hstatus, setHstatus] = useState(ALL);
  const [eventType, setEventType] = useState('');
  const [eventTypeDeb, setEventTypeDeb] = useState('');
  const [from, setFrom] = useState<string>();
  const [to, setTo] = useState<string>();
  useEffect(() => { const t = setTimeout(() => setEventTypeDeb(eventType.trim()), 400); return () => clearTimeout(t); }, [eventType]);

  useEffect(() => { notificationsApi.listSubscriptions().then(setSubs); }, []);
  useEffect(() => {
    setLoading(true);
    notificationsApi.history({
      channel: channel === ALL ? undefined : channel,
      status: hstatus === ALL ? undefined : hstatus,
      eventType: eventTypeDeb || undefined,
      from, to,
      pageSize: 100
    }).then(h => { setHistory(h.items); setLoading(false); });
  }, [channel, hstatus, eventTypeDeb, from, to]);

  function clearAll() { setChannel(ALL); setHstatus(ALL); setEventType(''); setFrom(undefined); setTo(undefined); }
  const active = [
    channel !== ALL && { key: 'c', label: t('ptNotifications.chipChannel'), value: channel, onClear: () => setChannel(ALL) },
    hstatus !== ALL && { key: 's', label: t('ptNotifications.chipStatus'), value: hstatus, onClear: () => setHstatus(ALL) },
    eventTypeDeb && { key: 'e', label: t('ptNotifications.chipEvent'), value: eventTypeDeb, onClear: () => setEventType('') },
    from && { key: 'f', label: t('ptNotifications.chipFrom'), value: formatDate(from, 'dd/MM HH:mm'), onClear: () => setFrom(undefined) },
    to && { key: 't', label: t('ptNotifications.chipTo'), value: formatDate(to, 'dd/MM HH:mm'), onClear: () => setTo(undefined) }
  ].filter(Boolean) as { key: string; label: string; value: string; onClear: () => void }[];
  return (
    <div>
      <PageHeader title={t('notifications.title')} actions={<JsonToggle data={{ subs, history }} label={t('ptNotifications.viewJson')} />} />
      <Tabs defaultValue="subs">
        <TabsList>
          <TabsTrigger value="subs">{t('ptNotifications.subsTab')}</TabsTrigger>
          <TabsTrigger value="history">{t('ptNotifications.historyTab')}</TabsTrigger>
        </TabsList>
        <TabsContent value="subs">
          <Card><Table>
            <TableHeader><TableRow><TableHead>{t('ptNotifications.colEvent')}</TableHead><TableHead>{t('ptNotifications.colChannel')}</TableHead><TableHead>{t('ptNotifications.colTarget')}</TableHead><TableHead>{t('ptNotifications.colActive')}</TableHead></TableRow></TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={4}><Loader /></TableCell></TableRow>}
              {!loading && subs.length === 0 && <TableRow><TableCell colSpan={4}><EmptyState /></TableCell></TableRow>}
              {subs.map((s: any, i: number) => (
                <TableRow key={bsonId(s._id, i)}>
                  <TableCell>{s.eventType ?? '—'}</TableCell>
                  <TableCell><Badge>{s.channel ?? '—'}</Badge></TableCell>
                  <TableCell className="text-xs"><Truncated mono value={s.target} maxChars={60} /></TableCell>
                  <TableCell>{s.isActive ? <Badge variant="success">ON</Badge> : <Badge variant="outline">OFF</Badge>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table></Card>
        </TabsContent>
        <TabsContent value="history">
          <FilterBar active={active} onClearAll={clearAll}>
            <Input className="h-8 w-48" placeholder={t('ptNotifications.eventTypePlaceholder')} value={eventType} onChange={e => setEventType(e.target.value)} />
            <Select value={channel} onValueChange={setChannel}>
              <SelectTrigger className="h-8 w-36 text-xs"><SelectValue placeholder={t('ptNotifications.chipChannel')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t('ptNotifications.anyChannel')}</SelectItem>
                <SelectItem value="email">{t('ptNotifications.chEmail')}</SelectItem>
                <SelectItem value="discord">{t('ptNotifications.chDiscord')}</SelectItem>
                <SelectItem value="telegram">{t('ptNotifications.chTelegram')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={hstatus} onValueChange={setHstatus}>
              <SelectTrigger className="h-8 w-36 text-xs"><SelectValue placeholder={t('ptNotifications.chipStatus')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t('ptNotifications.anyStatus')}</SelectItem>
                <SelectItem value="sent">{t('ptNotifications.stSent')}</SelectItem>
                <SelectItem value="failed">{t('ptNotifications.stFailed')}</SelectItem>
                <SelectItem value="queued">{t('ptNotifications.stQueued')}</SelectItem>
              </SelectContent>
            </Select>
            <DateRangePicker from={from} to={to} onChange={r => { setFrom(r.from); setTo(r.to); }} label={t('ptNotifications.dateLabel')} />
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => { const r = presetRange('last24h'); setFrom(r.from); setTo(r.to); }}>{t('ptMarks.preset24h')}</Button>
              <Button size="sm" variant="ghost" onClick={() => { const r = presetRange('last7d'); setFrom(r.from); setTo(r.to); }}>{t('ptMarks.preset7d')}</Button>
            </div>
          </FilterBar>
          <Card><Table>
            <TableHeader><TableRow><TableHead>{t('ptNotifications.colDate')}</TableHead><TableHead>{t('ptNotifications.colEvent')}</TableHead><TableHead>{t('ptNotifications.colChannel')}</TableHead><TableHead>{t('ptNotifications.colStatus')}</TableHead><TableHead>{t('ptNotifications.colTarget')}</TableHead></TableRow></TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={5}><Loader /></TableCell></TableRow>}
              {!loading && history.length === 0 && <TableRow><TableCell colSpan={5}><EmptyState /></TableCell></TableRow>}
              {history.map((h: any, i) => (
                <TableRow key={bsonId(h._id, i)}>
                  <TableCell className="text-xs whitespace-nowrap">{formatDate(h.sentAtUtc)}</TableCell>
                  <TableCell className="text-xs">{h.eventType ?? '—'}</TableCell>
                  <TableCell><Badge>{h.channel ?? '—'}</Badge></TableCell>
                  <TableCell>
                    <Badge variant={h.status === 'sent' ? 'success' : h.status === 'failed' ? 'destructive' : 'warning'}>
                      {h.status === 'sent' ? t('ptNotifications.stSent') : h.status === 'failed' ? t('ptNotifications.stFailed') : h.status === 'queued' ? t('ptNotifications.stQueued') : (h.status ?? '—')}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs"><Truncated mono value={h.target} maxChars={40} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============ REPORTS ============
export function ReportsPage() {
  const { t } = useTranslation();
  const [type, setType] = useState('cheater_dossier');
  const [entityType, setEntityType] = useState('hwid');
  const [entityValue, setEntityValue] = useState('');
  const [jobs, setJobs] = useState<any[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [filterType, setFilterType] = useState(ALL);
  const [filterStatus, setFilterStatus] = useState(ALL);

  async function reload() {
    setLoadingJobs(true);
    try {
      const r = await reportsApi.list({
        reportType: filterType === ALL ? undefined : filterType,
        status: filterStatus === ALL ? undefined : filterStatus,
        pageSize: 50
      });
      setJobs(r.items);
    } catch (e: any) { toast.error(t('ptReports.failedList'), e.message); }
    finally { setLoadingJobs(false); }
  }
  useEffect(() => { reload(); }, [filterType, filterStatus]);

  function clearJobsFilters() { setFilterType(ALL); setFilterStatus(ALL); }
  const jobsActive = [
    filterType !== ALL && { key: 't', label: t('ptReports.chipType'), value: filterType, onClear: () => setFilterType(ALL) },
    filterStatus !== ALL && { key: 's', label: t('ptReports.chipStatus'), value: filterStatus, onClear: () => setFilterStatus(ALL) }
  ].filter(Boolean) as { key: string; label: string; value: string; onClear: () => void }[];

  async function generate() {
    try {
      const params = type === 'cheater_dossier' ? { entityType, entityValue } : {};
      const r = await reportsApi.generate(type, params);
      toast.success(t('ptReports.queued'), t('ptReports.queuedJob', { id: r.jobId }));
      setTimeout(reload, 1500);
    } catch (e: any) { toast.error(t('ptReports.failedGenerate'), e.message); }
  }
  return (
    <div>
      <PageHeader title={t('reports.title')} actions={<JsonToggle data={jobs} label={t('ptReports.viewJson')} />} />
      <div className="grid md:grid-cols-2 gap-4">
        <Card><CardHeader><CardTitle>{t('reports.generate')}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div><Label>{t('ptReports.type')}</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cheater_dossier">{t('reports.types.cheater_dossier')}</SelectItem>
                <SelectItem value="audit_log_csv">{t('reports.types.audit_log_csv')}</SelectItem>
                <SelectItem value="marks_export_csv">{t('reports.types.marks_export_csv')}</SelectItem>
                <SelectItem value="cases_export_csv">{t('reports.types.cases_export_csv')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {type === 'cheater_dossier' && (
            <>
              <div><Label>{t('ptReports.entityType')}</Label>
                <Select value={entityType} onValueChange={setEntityType}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{['hwid','user_id','file_md5','ip_address'].map(x => <SelectItem key={x} value={x}>{String(t(`marks.entityTypes.${x}`, { defaultValue: x }))}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>{t('ptReports.entityValue')}</Label><Input value={entityValue} onChange={e => setEntityValue(e.target.value)} placeholder={t('ptReports.entityValuePlaceholder')} /></div>
            </>
          )}
          <Button onClick={generate}>{t('ptReports.generate')}</Button>
        </CardContent></Card>

        <Card><CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('ptReports.recent')}</CardTitle>
            <Button size="sm" variant="outline" onClick={reload}>{t('ptReports.refresh')}</Button>
          </div>
        </CardHeader>
        <CardContent className="p-3">
          <FilterBar active={jobsActive} onClearAll={clearJobsFilters}>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="h-8 w-56 text-xs"><SelectValue placeholder={t('ptReports.filterTypePlaceholder')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t('ptReports.allTypes')}</SelectItem>
                <SelectItem value="cheater_dossier">{t('reports.types.cheater_dossier')}</SelectItem>
                <SelectItem value="audit_log_csv">{t('reports.types.audit_log_csv')}</SelectItem>
                <SelectItem value="marks_export_csv">{t('reports.types.marks_export_csv')}</SelectItem>
                <SelectItem value="cases_export_csv">{t('reports.types.cases_export_csv')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="h-8 w-36 text-xs"><SelectValue placeholder={t('ptReports.filterStatusPlaceholder')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t('ptReports.allStatuses')}</SelectItem>
                <SelectItem value="pending">{t('ptReports.stPending')}</SelectItem>
                <SelectItem value="running">{t('ptReports.stRunning')}</SelectItem>
                <SelectItem value="ready">{t('ptReports.stReady')}</SelectItem>
                <SelectItem value="failed">{t('ptReports.stFailed')}</SelectItem>
              </SelectContent>
            </Select>
          </FilterBar>
          <Table>
            <TableHeader><TableRow><TableHead>{t('ptReports.colType')}</TableHead><TableHead>{t('ptReports.colStatus')}</TableHead><TableHead>{t('ptReports.colCreatedAt')}</TableHead><TableHead className="sr-only">{t('ptReports.colActions')}</TableHead></TableRow></TableHeader>
            <TableBody>
              {loadingJobs && <TableRow><TableCell colSpan={4}><Loader /></TableCell></TableRow>}
              {!loadingJobs && jobs.length === 0 && <TableRow><TableCell colSpan={4}><EmptyState title={t('ptReports.emptyTitle')} /></TableCell></TableRow>}
              {jobs.map((j: any, i: number) => {
                const id = bsonId(j._id, i);
                const status = j.status;
                const statusLabel = status === 'ready' ? t('ptReports.stReady')
                  : status === 'failed' ? t('ptReports.stFailed')
                  : status === 'running' ? t('ptReports.stRunning')
                  : status === 'pending' ? t('ptReports.stPending')
                  : (status ?? '—');
                return (
                  <TableRow key={id || i}>
                    <TableCell className="text-xs">{j.reportType ? String(t(`reports.types.${j.reportType}`, { defaultValue: j.reportType })) : '—'}</TableCell>
                    <TableCell>
                      <Badge variant={status === 'ready' ? 'success' : status === 'failed' ? 'destructive' : status === 'running' ? 'warning' : 'outline'}>
                        {statusLabel}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{formatDate(j.createdAtUtc)}</TableCell>
                    <TableCell>
                      {status === 'ready' && id && (
                        <Button size="sm" variant="outline" onClick={async () => {
                          try {
                            const { url } = await reportsApi.requestDownloadUrl(id);
                            const a = document.createElement('a');
                            a.href = url;
                            a.target = '_blank';
                            a.rel = 'noreferrer';
                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                          } catch (e: any) {
                            const reason = e?.response?.data?.reason;
                            const friendly = reason === 'not_ready' ? t('ptReports.errNotReady')
                              : reason === 'report_not_found' ? t('ptReports.errNotFound')
                              : reason ?? e.message ?? t('ptReports.errUnexpected');
                            toast.error(t('ptReports.failedDownload'), friendly);
                          }
                        }}>{t('ptReports.downloadBtn')}</Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent></Card>
      </div>
    </div>
  );
}

// ============ CHANGELOG ============
export function ChangelogPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { changelogApi.list(50).then(d => { setItems(d); setLoading(false); }); }, []);
  return (
    <div>
      <PageHeader title={t('changelog.title')} />
      {loading && <Loader />}
      <div className="space-y-3 max-w-3xl">
        {!loading && items.length === 0 && <EmptyState title={t('ptChangelog.emptyTitle')} />}
        {items.map((e: any, idx: number) => (
          <Card key={e.version ?? e._id ?? idx}>
            <CardHeader>
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="flex items-center gap-2">
                  <Badge>{e.version ?? `v${idx + 1}`}</Badge>
                  <span>{e.title ?? e.category ?? '—'}</span>
                </CardTitle>
                <span className="text-xs text-muted-foreground">{formatDate(e.releasedAtUtc ?? e.createdAtUtc, 'dd/MM/yyyy')}</span>
              </div>
            </CardHeader>
            <CardContent>
              {e.summary && <p className="text-sm text-muted-foreground mb-3">{e.summary}</p>}
              <ul className="space-y-1">
                {(e.items ?? []).map((i: any, k: number) => (
                  <li key={k} className="flex items-start gap-2 text-sm">
                    <Badge variant="outline" className="font-mono text-[10px]">{i.type ?? '—'}</Badge>
                    <span>{i.text ?? ''}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ============ AUDIT ============
export function AuditPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actorEmail, setActorEmail] = useState('');
  const [actorEmailDeb, setActorEmailDeb] = useState('');
  const [action, setAction] = useState(ALL);
  const [ipAddress, setIpAddress] = useState('');
  const [ipAddressDeb, setIpAddressDeb] = useState('');
  const [from, setFrom] = useState<string>();
  const [to, setTo] = useState<string>();

  useEffect(() => { const t = setTimeout(() => setActorEmailDeb(actorEmail.trim()), 400); return () => clearTimeout(t); }, [actorEmail]);
  useEffect(() => { const t = setTimeout(() => setIpAddressDeb(ipAddress.trim()), 400); return () => clearTimeout(t); }, [ipAddress]);

  useEffect(() => {
    setLoading(true);
    auditApi.list({
      actorEmail: actorEmailDeb || undefined,
      action: action === ALL ? undefined : action,
      ipAddress: ipAddressDeb || undefined,
      from, to,
      pageSize: 100
    }).then(d => { setItems(d.items); setTotal(d.totalApprox ?? d.items.length); setLoading(false); });
  }, [actorEmailDeb, action, ipAddressDeb, from, to]);

  function clearAll() { setActorEmail(''); setAction(ALL); setIpAddress(''); setFrom(undefined); setTo(undefined); }
  const active = [
    actorEmailDeb && { key: 'a', label: t('ptAudit.chipActor'), value: actorEmailDeb, onClear: () => setActorEmail('') },
    action !== ALL && { key: 'm', label: t('ptAudit.chipMethod'), value: action, onClear: () => setAction(ALL) },
    ipAddressDeb && { key: 'i', label: t('ptAudit.chipIp'), value: ipAddressDeb, onClear: () => setIpAddress('') },
    from && { key: 'f', label: t('ptAudit.chipFrom'), value: formatDate(from, 'dd/MM HH:mm'), onClear: () => setFrom(undefined) },
    to && { key: 't', label: t('ptAudit.chipTo'), value: formatDate(to, 'dd/MM HH:mm'), onClear: () => setTo(undefined) }
  ].filter(Boolean) as { key: string; label: string; value: string; onClear: () => void }[];

  return (
    <div>
      <PageHeader title={t('nav.audit')} subtitle={t('ptAudit.subtitle', { count: total })} actions={<JsonToggle data={items} label={t('ptAudit.viewJson')} />} />
      <FilterBar active={active} onClearAll={clearAll}>
        <Input className="h-8 w-56" placeholder={t('ptAudit.actorPlaceholder')} value={actorEmail} onChange={e => setActorEmail(e.target.value)} />
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger className="h-8 w-36 text-xs"><SelectValue placeholder={t('ptAudit.methodPlaceholder')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('ptAudit.anyMethod')}</SelectItem>
            <SelectItem value="POST">POST</SelectItem>
            <SelectItem value="PUT">PUT</SelectItem>
            <SelectItem value="PATCH">PATCH</SelectItem>
            <SelectItem value="DELETE">DELETE</SelectItem>
          </SelectContent>
        </Select>
        <Input className="h-8 w-40" placeholder={t('ptAudit.ipPlaceholder')} value={ipAddress} onChange={e => setIpAddress(e.target.value)} />
        <DateRangePicker from={from} to={to} onChange={r => { setFrom(r.from); setTo(r.to); }} label={t('ptAudit.dateLabel')} />
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" onClick={() => { const r = presetRange('last1h'); setFrom(r.from); setTo(r.to); }}>{t('ptAudit.preset1h')}</Button>
          <Button size="sm" variant="ghost" onClick={() => { const r = presetRange('last24h'); setFrom(r.from); setTo(r.to); }}>{t('ptAudit.preset24h')}</Button>
          <Button size="sm" variant="ghost" onClick={() => { const r = presetRange('last7d'); setFrom(r.from); setTo(r.to); }}>{t('ptAudit.preset7d')}</Button>
        </div>
      </FilterBar>
      <Card><Table>
        <TableHeader><TableRow><TableHead>{t('ptAudit.colDate')}</TableHead><TableHead>{t('ptAudit.colActor')}</TableHead><TableHead>{t('ptAudit.colMethod')}</TableHead><TableHead>{t('ptAudit.colHttp')}</TableHead><TableHead>{t('ptAudit.colPath')}</TableHead><TableHead>{t('ptAudit.colIp')}</TableHead></TableRow></TableHeader>
        <TableBody>
          {loading && <TableRow><TableCell colSpan={6}><Loader /></TableCell></TableRow>}
          {!loading && items.length === 0 && <TableRow><TableCell colSpan={6}><EmptyState /></TableCell></TableRow>}
          {items.map((a: any, i: number) => (
            <TableRow key={bsonId(a._id, i)}>
              <TableCell className="text-xs whitespace-nowrap">{formatDate(a.createdAtUtc)}</TableCell>
              <TableCell className="text-xs">{a.actorEmail || '—'}</TableCell>
              <TableCell><Badge variant="outline">{a.method ?? '—'}</Badge></TableCell>
              <TableCell><Badge variant={(a.statusCode ?? 0) < 400 ? 'success' : 'destructive'}>{a.statusCode ?? '—'}</Badge></TableCell>
              <TableCell className="text-xs"><Truncated mono value={a.path} maxChars={50} /></TableCell>
              <TableCell className="text-xs"><Truncated mono value={a.ipAddress} maxChars={20} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table></Card>
    </div>
  );
}

// ============ CONFIG GLOBAL — edit dialog =============================
function detectType(v: any): 'string' | 'number' | 'boolean' | 'json' {
  if (typeof v === 'number') return 'number';
  if (typeof v === 'boolean') return 'boolean';
  if (typeof v === 'string') return 'string';
  return 'json';
}

function EditSettingButton({ setting, onSaved }: { setting: any; onSaved: () => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [raw, setRaw] = useState('');
  const [loading, setLoading] = useState(false);
  const typeHint = setting.typeHint as ('string' | 'number' | 'boolean' | 'json' | undefined) ?? detectType(setting.value);

  function init() {
    if (typeHint === 'string') setRaw(setting.value ?? '');
    else setRaw(JSON.stringify(setting.value ?? null, null, 2));
  }

  async function save() {
    setLoading(true);
    try {
      let parsed: any;
      if (typeHint === 'string') parsed = raw;
      else if (typeHint === 'number') {
        const n = Number(raw);
        if (!Number.isFinite(n)) throw new Error(t('ptConfigGlobal.errInvalidNumber'));
        parsed = n;
      } else if (typeHint === 'boolean') {
        const low = raw.trim().toLowerCase();
        if (['true', '1', 'sim', 'on', 'yes'].includes(low)) parsed = true;
        else if (['false', '0', 'nao', 'não', 'off', 'no'].includes(low)) parsed = false;
        else throw new Error(t('ptConfigGlobal.errInvalidBool'));
      } else {
        parsed = JSON.parse(raw);
      }
      await configApi.updateSetting(setting.key, parsed);
      toast.success(t('ptConfigGlobal.okSaved'));
      setOpen(false);
      onSaved();
    } catch (e: any) {
      toast.error(t('ptConfigGlobal.errSave'), e?.response?.data?.reason ?? e.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) init(); }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" title={t('ptConfigGlobal.editBtn')}>{t('ptConfigGlobal.editBtn')}</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{t('ptConfigGlobal.editTitle')}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>{t('ptConfigGlobal.keyLabel')}</Label><Input value={setting.key ?? ''} readOnly className="font-mono bg-muted" /></div>
          <div><Label>{t('ptConfigGlobal.categoryLabel')}</Label><Input value={setting.category ?? ''} readOnly className="bg-muted" /></div>
          {setting.description && <div className="text-xs text-muted-foreground">{setting.description}</div>}
          <div>
            <Label>{t('ptConfigGlobal.valueLabel')} ({typeHint})</Label>
            {typeHint === 'boolean' ? (
              <Select value={raw === 'true' ? 'true' : raw === 'false' ? 'false' : raw} onValueChange={setRaw}>
                <SelectTrigger><SelectValue placeholder={t('ptConfigGlobal.boolPlaceholder')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">{t('ptConfigGlobal.boolTrue')}</SelectItem>
                  <SelectItem value="false">{t('ptConfigGlobal.boolFalse')}</SelectItem>
                </SelectContent>
              </Select>
            ) : typeHint === 'json' ? (
              <Textarea value={raw} onChange={e => setRaw(e.target.value)} rows={8} className="font-mono text-xs" />
            ) : (
              <Input value={raw} onChange={e => setRaw(e.target.value)} type={typeHint === 'number' ? 'number' : 'text'} className={typeHint === 'string' ? '' : 'font-mono'} />
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>{t('ptConfigGlobal.cancelBtn')}</Button>
          <Button onClick={save} disabled={loading}>{loading ? <Loader /> : t('ptConfigGlobal.saveBtn')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============ CONFIG GLOBAL ============
export function ConfigGlobalPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { configApi.listSettings().then(d => { setItems(d); setLoading(false); }); }, []);
  return (
    <div>
      <PageHeader title={t('nav.configGlobal')} actions={<JsonToggle data={items} label={t('ptConfigGlobal.viewJson')} />} />
      <Card><Table>
        <TableHeader><TableRow><TableHead>{t('ptConfigGlobal.colCategory')}</TableHead><TableHead>{t('ptConfigGlobal.colKey')}</TableHead><TableHead>{t('ptConfigGlobal.colValue')}</TableHead><TableHead>{t('ptConfigGlobal.colDescription')}</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>
          {loading && <TableRow><TableCell colSpan={5}><Loader /></TableCell></TableRow>}
          {!loading && items.length === 0 && <TableRow><TableCell colSpan={5}><EmptyState /></TableCell></TableRow>}
          {items.map((s: any, i: number) => (
            <TableRow key={s.key ?? bsonId(s._id, i)}>
              <TableCell><Badge variant="outline">{s.category ?? '—'}</Badge></TableCell>
              <TableCell className="font-mono text-xs">{s.key ?? '—'}</TableCell>
              <TableCell className="text-xs"><Truncated mono value={JSON.stringify(s.value)} maxChars={60} /></TableCell>
              <TableCell className="text-xs text-muted-foreground">{s.description ?? '—'}</TableCell>
              <TableCell><EditSettingButton setting={s} onSaved={() => configApi.listSettings().then(setItems)} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table></Card>
    </div>
  );
}

// ============ SETTINGS (profile + theme + 2fa) ============
export function SettingsPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState('profile');
  return (
    <div>
      <PageHeader title={t('settings.title')} />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="profile">{t('settings.profile')}</TabsTrigger>
          <TabsTrigger value="security">{t('settings.security')}</TabsTrigger>
          <TabsTrigger value="preferences">{t('settings.preferences')}</TabsTrigger>
        </TabsList>
        <TabsContent value="profile">
          <ProfileForm />
          <DiscordLinkCard />
        </TabsContent>
        <TabsContent value="security">
          <Card><CardHeader><CardTitle>{t('settings.changePassword')}</CardTitle></CardHeader>
          <ChangePasswordForm />
          </Card>
          <Card className="mt-3"><CardHeader><CardTitle>{t('settings.twoFactor')}</CardTitle></CardHeader>
          <TotpManager />
          </Card>
          <TrustedIpCard />
        </TabsContent>
        <TabsContent value="preferences">
          <Card><CardContent className="pt-6 text-sm text-muted-foreground">
            {t('ptSettings.preferences')}
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ProfileForm() {
  const { t } = useTranslation();
  const { user, setUser, clearTokens } = useAuthStore();
  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setDisplayName(user?.displayName ?? '');
    setEmail(user?.email ?? '');
  }, [user?.displayName, user?.email]);

  const dirty = displayName.trim() !== (user?.displayName ?? '') || email.trim().toLowerCase() !== (user?.email ?? '').toLowerCase();

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!dirty || loading) return;
    setLoading(true);
    try {
      const body: { displayName?: string; email?: string } = {};
      if (displayName.trim() !== (user?.displayName ?? '')) body.displayName = displayName.trim();
      if (email.trim().toLowerCase() !== (user?.email ?? '').toLowerCase()) body.email = email.trim();
      const updated = await authApi.updateMe(body);
      if (user) setUser({ ...user, displayName: updated.displayName, email: updated.email });
      toast.success(t('ptSettings.profileUpdated'));
      if (updated.emailChanged) {
        toast.info(t('ptSettings.emailChangedTitle'), t('ptSettings.emailChangedHint'));
        setTimeout(() => { clearTokens(); window.location.href = '/login'; }, 1500);
      }
    } catch (e: any) {
      toast.error(t('ptSettings.profileSaveFailed'), e?.response?.data?.reason ?? e.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Card><CardContent className="pt-6">
      <form onSubmit={save} className="space-y-3 max-w-lg">
        <div>
          <Label>{t('ptSettings.emailLabel')}</Label>
          <Input type="email" value={email} onChange={e => setEmail(e.target.value)} required />
          <div className="text-xs text-muted-foreground mt-1">{t('ptSettings.emailHint')}</div>
        </div>
        <div>
          <Label>{t('ptSettings.displayNameLabel')}</Label>
          <Input value={displayName} onChange={e => setDisplayName(e.target.value)} required />
        </div>
        <div>
          <Label>{t('ptSettings.roleLabel')}</Label>
          <Input value={user?.role ? String(t(`users.roles.${user.role}`, { defaultValue: user.role })) : ''} readOnly className="bg-muted" />
          <div className="text-xs text-muted-foreground mt-1">{t('ptSettings.roleReadonlyHint')}</div>
        </div>
        <div className="flex gap-2 pt-2">
          <Button type="submit" disabled={!dirty || loading}>{loading ? <Loader /> : t('ptSettings.saveChanges')}</Button>
          {dirty && <Button type="button" variant="ghost" onClick={() => { setDisplayName(user?.displayName ?? ''); setEmail(user?.email ?? ''); }}>{t('ptSettings.discard')}</Button>}
        </div>
      </form>
    </CardContent></Card>
  );
}

function ChangePasswordForm() {
  const { t } = useTranslation();
  const [curr, setCurr] = useState('');
  const [next, setNext] = useState('');
  const [loading, setLoading] = useState(false);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      await authApi.changePassword({ currentPassword: curr, newPassword: next });
      toast.success(t('ptSettings.passwordUpdated'));
      setCurr(''); setNext('');
    } catch (e: any) { toast.error(e?.response?.data?.reason ?? e.message); }
    finally { setLoading(false); }
  }
  return (
    <form onSubmit={submit} className="pb-6 px-6 space-y-3">
      <div><Label>{t('ptSettings.currentPassword')}</Label><Input type="password" value={curr} onChange={e => setCurr(e.target.value)} required /></div>
      <div><Label>{t('ptSettings.newPasswordLabel')}</Label><Input type="password" value={next} onChange={e => setNext(e.target.value)} required minLength={8} /></div>
      <Button type="submit" disabled={loading}>{loading ? <Loader /> : t('ptSettings.updatePasswordBtn')}</Button>
    </form>
  );
}

function TotpManager() {
  const { t } = useTranslation();
  const { user, setUser } = useAuthStore();
  const [setup, setSetup] = useState<any>(null);
  const [code, setCode] = useState('');
  async function start() { setSetup(await authApi.setupTotp()); }
  async function confirm() {
    try { await authApi.confirmTotp(code); toast.success(t('ptSettings.totpEnabledOk')); setSetup(null); setCode(''); if (user) setUser({ ...user, isTotpEnabled: true }); }
    catch (e: any) { toast.error(e?.response?.data?.reason ?? e.message); }
  }
  async function disable() {
    try { await authApi.disableTotp(code); toast.success(t('ptSettings.totpDisabledOk')); setCode(''); if (user) setUser({ ...user, isTotpEnabled: false }); }
    catch (e: any) { toast.error(e?.response?.data?.reason ?? e.message); }
  }
  return (
    <CardContent className="space-y-3">
      {user?.isTotpEnabled ? (
        <div>
          <p className="text-sm">{t('ptSettings.totpEnabled')} <Badge variant="success">{t('ptSettings.totpActivated')}</Badge></p>
          <div className="mt-3 flex gap-2 items-end">
            <div className="flex-1"><Label>{t('ptSettings.totpCurrentCode')}</Label><Input value={code} onChange={e => setCode(e.target.value)} maxLength={6} /></div>
            <Button variant="destructive" onClick={disable}>{t('ptSettings.totpDisableBtn')}</Button>
          </div>
        </div>
      ) : setup ? (
        <div className="space-y-3">
          <p className="text-sm">{t('ptSettings.totpScanQr')}</p>
          <img src={`data:image/png;base64,${setup.qrCodePngBase64}`} alt="QR Code" className="border rounded p-2 bg-white inline-block" />
          <div><Label>{t('ptSettings.totpSecretLabel')}</Label><Input value={setup.secretBase32} readOnly className="font-mono" /></div>
          <div><Label>{t('ptSettings.totpBackupLabel')}</Label>
            <pre className="text-xs bg-muted p-3 rounded">{(setup.backupCodes || []).join('\n')}</pre>
          </div>
          <div className="flex gap-2 items-end">
            <div className="flex-1"><Label>{t('ptSettings.totpAppCode')}</Label><Input value={code} onChange={e => setCode(e.target.value)} maxLength={6} placeholder="000000" /></div>
            <Button onClick={confirm}>{t('ptSettings.totpConfirmBtn')}</Button>
          </div>
        </div>
      ) : <Button onClick={start}>{t('ptSettings.totpActivateBtn')}</Button>}
    </CardContent>
  );
}

// ============ Discord link ============
// Card no /settings (aba Perfil). Se o user nao tem Discord vinculado, mostra
// botao "Vincular Discord" que abre popup OAuth. Se ja tem, mostra avatar +
// username com botao "Desvincular".
function DiscordLinkCard() {
  const { t } = useTranslation();
  const { user, setUser } = useAuthStore();
  const [me, setMe] = useState<any>(user);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    authApi.me().then((res: any) => {
      setMe(res);
      if (user) setUser({ ...user, ...res });
    }).catch(() => { /* ignore */ });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    const status = url.searchParams.get('discord');
    const err = url.searchParams.get('discord_error');
    if (status === 'linked') {
      toast.success(t('ptSettings.discordLinked'));
      url.searchParams.delete('discord');
      window.history.replaceState({}, '', url.pathname + url.search);
    } else if (status === 'error' || err) {
      toast.error(t('ptSettings.discordFailLink'), err || t('common.error'));
      url.searchParams.delete('discord');
      url.searchParams.delete('discord_error');
      window.history.replaceState({}, '', url.pathname + url.search);
    }
    // eslint-disable-next-line
  }, []);

  async function startLink() {
    setBusy(true);
    try {
      const { authorizeUrl } = await authApi.discordLinkStart();
      window.location.href = authorizeUrl;
    } catch (e: any) {
      toast.error(t('ptSettings.discordCannotStart'), e?.response?.data?.reason || e?.message);
      setBusy(false);
    }
  }

  async function unlink() {
    if (!confirm(t('ptSettings.discordConfirmUnlink'))) return;
    setBusy(true);
    try {
      await authApi.discordUnlink();
      toast.success(t('ptSettings.discordUnlinked'));
      const fresh = await authApi.me();
      setMe(fresh);
      if (user) setUser({ ...user, ...fresh });
    } catch (e: any) {
      toast.error(t('ptSettings.discordFailUnlink'), e?.response?.data?.reason || e?.message);
    } finally {
      setBusy(false);
    }
  }

  const discord = (me as any)?.discord;
  const avatarUrl = discord?.avatar
    ? `https://cdn.discordapp.com/avatars/${discord.id}/${discord.avatar}.png?size=64`
    : 'https://cdn.discordapp.com/embed/avatars/0.png';

  return (
    <Card className="mt-3">
      <CardHeader><CardTitle>{t('ptSettings.discordTitle')}</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {discord ? (
          <div className="flex items-center gap-3">
            <img src={avatarUrl} alt="" className="w-12 h-12 rounded-full" />
            <div className="flex-1 min-w-0">
              <div className="font-medium">{discord.username}</div>
              <div className="text-xs text-muted-foreground">
                ID: <span className="font-mono">{discord.id}</span>
                {discord.email && <> · {discord.email}</>}
              </div>
              {discord.linkedAtUtc && (
                <div className="text-xs text-muted-foreground">{t('ptSettings.discordLinkedAt', { time: new Date(discord.linkedAtUtc).toLocaleString() })}</div>
              )}
            </div>
            <Button variant="ghost" onClick={unlink} disabled={busy}>{t('ptSettings.discordUnlink')}</Button>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <div className="flex-1 text-sm text-muted-foreground">{t('ptSettings.discordEmpty')}</div>
            <Button onClick={startLink} disabled={busy}>{t('ptSettings.discordLink')}</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TrustedIpCard() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [me, setMe] = useState<any>(user);
  useEffect(() => {
    authApi.me().then(setMe).catch(() => { /* ok */ });
  }, []);
  const ip = (me as any)?.trustedIp;
  const setAt = (me as any)?.trustedIpSetAtUtc;
  return (
    <Card className="mt-3">
      <CardHeader><CardTitle>{t('ptSettings.trustedIpTitle')}</CardTitle></CardHeader>
      <CardContent className="text-sm space-y-2">
        <div>{t('ptSettings.trustedIpDesc')}</div>
        <div className="bg-muted/50 rounded p-3 space-y-1">
          <div><span className="text-muted-foreground">{t('ptSettings.trustedIpCurrent')}</span> <span className="font-mono font-medium">{ip ?? t('ptSettings.trustedIpNone')}</span></div>
          {setAt && <div className="text-xs text-muted-foreground">{t('ptSettings.trustedIpSetAt', { time: new Date(setAt).toLocaleString() })}</div>}
        </div>
        <div className="text-xs text-muted-foreground">{t('ptSettings.trustedIpRotate')}</div>
      </CardContent>
    </Card>
  );
}
